/**
 * Everything the run produced, one tab per artefact.
 *
 * Two deliberate choices:
 *
 * 1. Markdown and text are shown RAW, not rendered. What a RAG pipeline
 *    receives is the markdown itself — its table pipes, its page separators,
 *    its escaping — and a prettified view hides exactly the details someone
 *    comes here to check.
 *
 * 2. The HTML artefact IS rendered, because its whole point is table
 *    structure, which is unreadable as source. It renders inside a sandboxed
 *    iframe with no scripts and no same-origin access: this markup is derived
 *    from a PDF someone uploaded, so it is treated as untrusted content rather
 *    than as our own.
 */

import { useEffect, useMemo, useState } from "react";
import { FiCopy, FiDownload } from "react-icons/fi";
import { Button, Card, EmptyState, Tabs } from "@/components/ui";
import { prettyBytes, type ConvertResult } from "@/lib/pdflab";

type TabId =
  | "markdown" | "html" | "text" | "json"
  | "annotatedPdf" | "taggedPdf" | "images" | "command";

const TAB_LABEL: Record<TabId, string> = {
  markdown: "Markdown",
  html: "HTML",
  text: "Text",
  json: "JSON",
  annotatedPdf: "Annotated PDF",
  taggedPdf: "Tagged PDF",
  images: "Images",
  command: "Command",
};

export function OutputTabs({ result }: { result: ConvertResult }) {
  const outputs = result.outputs ?? {};

  const available = useMemo(() => {
    const ids: TabId[] = [];
    for (const id of ["markdown", "html", "text", "json", "annotatedPdf", "taggedPdf"] as TabId[]) {
      if ((outputs as any)[id] !== undefined) ids.push(id);
    }
    if (result.images?.length) ids.push("images");
    // Always last, always present: the exact argv is the thing you want when a
    // result surprises you, and a tab that sometimes vanishes is worse than one
    // that is sometimes empty.
    ids.push("command");
    return ids;
  }, [outputs, result.images]);

  const [tab, setTab] = useState<TabId>(available[0] ?? "command");
  useEffect(() => {
    if (!available.includes(tab)) setTab(available[0] ?? "command");
  }, [available, tab]);

  if (!available.length) return <EmptyState title="Nothing was produced" />;

  return (
    <div className="space-y-3">
      <Tabs
        tabs={available.map((id) => ({
          id,
          label: TAB_LABEL[id],
          count: id === "images" ? result.images?.length : undefined,
        }))}
        active={tab}
        onChange={setTab}
      />

      {tab === "markdown" && <TextArtefact text={outputs.markdown ?? ""} filename="output.md" />}
      {tab === "text" && <TextArtefact text={outputs.text ?? ""} filename="output.txt" />}
      {tab === "json" && (
        <TextArtefact text={JSON.stringify(outputs.json, null, 2)} filename="output.json" />
      )}
      {tab === "html" && <HtmlArtefact html={outputs.html ?? ""} />}
      {tab === "annotatedPdf" && (
        <PdfArtefact
          base64={outputs.annotatedPdf!}
          filename="annotated.pdf"
          blurb="The source PDF with the parser's boxes drawn on it — the parse as the parser saw it, in the document's own coordinates."
        />
      )}
      {tab === "taggedPdf" && (
        <PdfArtefact
          base64={outputs.taggedPdf!}
          filename="tagged.pdf"
          blurb="The source PDF with an accessibility structure tree written into it (PDF/UA tags). This is the auto-tagging output, not a visual change — open it in a reader's tag inspector."
        />
      )}
      {tab === "images" && <ImagesArtefact images={result.images ?? []} />}
      {tab === "command" && <CommandArtefact result={result} />}
    </div>
  );
}

function download(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  // Revoked on the next tick: revoking synchronously can cancel the download in
  // some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function TextArtefact({ text, filename }: { text: string; filename: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-fh-border-soft px-4 py-2">
        <span className="text-fh-meta text-fh-text-muted">
          {text.length.toLocaleString()} characters
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              navigator.clipboard.writeText(text);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            <FiCopy className="mr-1.5" /> {copied ? "Copied" : "Copy"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => download(filename, new Blob([text], { type: "text/plain" }))}
          >
            <FiDownload className="mr-1.5" /> Download
          </Button>
        </div>
      </div>
      <pre className="max-h-[65vh] overflow-auto px-4 py-3 text-fh-meta leading-relaxed whitespace-pre-wrap break-words font-mono text-fh-text-body">
        {text || "(empty)"}
      </pre>
    </Card>
  );
}

function HtmlArtefact({ html }: { html: string }) {
  const [mode, setMode] = useState<"rendered" | "source">("rendered");
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-fh-border-soft px-4 py-2">
        <Tabs
          tabs={[
            { id: "rendered" as const, label: "Rendered" },
            { id: "source" as const, label: "Source" },
          ]}
          active={mode}
          onChange={setMode}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => download("output.html", new Blob([html], { type: "text/html" }))}
        >
          <FiDownload className="mr-1.5" /> Download
        </Button>
      </div>
      {mode === "rendered" ? (
        <iframe
          // sandbox with NO tokens: no scripts, no same-origin, no forms. This
          // markup came out of a PDF a user uploaded; it is displayed, never
          // trusted. Removing a token here is a security change, not styling.
          sandbox=""
          srcDoc={html}
          title="HTML output"
          className="h-[65vh] w-full border-0 bg-white"
        />
      ) : (
        <pre className="max-h-[65vh] overflow-auto px-4 py-3 text-fh-meta leading-relaxed whitespace-pre-wrap break-words font-mono text-fh-text-body">
          {html}
        </pre>
      )}
    </Card>
  );
}

function PdfArtefact({
  base64,
  filename,
  blurb,
}: {
  base64: string;
  filename: string;
  blurb: string;
}) {
  const url = useMemo(() => {
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  }, [base64]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b border-fh-border-soft px-4 py-2.5">
        <p className="max-w-prose text-fh-meta leading-relaxed text-fh-text-muted">{blurb}</p>
        {/* A plain anchor, not a Button: the blob URL is already the file, so
            re-encoding it through the download helper would only be a second
            copy of the same bytes. */}
        <a
          href={url}
          download={filename}
          className="fh-focus flex shrink-0 items-center rounded-fh-input px-3 py-1.5 text-fh-cell text-fh-text-secondary transition hover:bg-fh-surface-soft hover:text-fh-text-mid"
        >
          <FiDownload className="mr-1.5" /> Download
        </a>
      </div>
      <iframe src={url} title={filename} className="h-[65vh] w-full border-0 bg-fh-surface-soft" />
    </Card>
  );
}

function ImagesArtefact({
  images,
}: {
  images: Array<{ name: string; bytes: number; base64?: string; skipped?: string }>;
}) {
  return (
    <Card className="p-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {images.map((img) => (
          <figure key={img.name} className="min-w-0">
            {img.base64 ? (
              <img
                src={`data:image/png;base64,${img.base64}`}
                alt={img.name}
                className="w-full rounded-fh-input bg-fh-surface-soft object-contain shadow-sm"
              />
            ) : (
              <div className="grid h-32 place-items-center rounded-fh-input bg-fh-surface-soft text-fh-meta text-fh-text-muted">
                {img.skipped ?? "not inlined"}
              </div>
            )}
            <figcaption className="mt-1 truncate text-fh-meta text-fh-text-muted">
              {img.name} · {prettyBytes(img.bytes)}
            </figcaption>
          </figure>
        ))}
      </div>
    </Card>
  );
}

function CommandArtefact({ result }: { result: ConvertResult }) {
  return (
    <div className="space-y-3">
      <Card className="overflow-hidden">
        <div className="border-b border-fh-border-soft px-4 py-2">
          <span className="text-fh-row font-medium text-fh-text-strong">What ran</span>
          <p className="mt-0.5 text-fh-meta leading-relaxed text-fh-text-muted">
            The exact argv, temp paths included, so it can be pasted into a shell against the same
            engine version ({result.version ?? "unknown"}). Exit code {result.returncode ?? "?"}.
          </p>
        </div>
        <pre className="overflow-auto px-4 py-3 text-fh-meta leading-relaxed whitespace-pre-wrap break-all font-mono text-fh-text-body">
          {(result.command ?? []).join(" ") || "(not recorded)"}
        </pre>
      </Card>

      {result.stdout && (
        <Card className="overflow-hidden">
          <div className="border-b border-fh-border-soft px-4 py-2">
            <span className="text-fh-row font-medium text-fh-text-strong">Engine output</span>
            <p className="mt-0.5 text-fh-meta leading-relaxed text-fh-text-muted">
              The parser's stdout, shown separately from the log below because that is where it
              puts the one line that says why it refused. Its stderr is a logging stream —
              hundreds of font warnings on a real document — so a reason buried in there is a
              reason nobody finds.
            </p>
          </div>
          <pre className="max-h-64 overflow-auto px-4 py-3 text-fh-meta leading-relaxed whitespace-pre-wrap break-words font-mono text-fh-text-body">
            {result.stdout}
          </pre>
        </Card>
      )}

      {result.log && (
        <Card className="overflow-hidden">
          <div className="border-b border-fh-border-soft px-4 py-2">
            <span className="text-fh-row font-medium text-fh-text-strong">Engine log</span>
          </div>
          <pre className="max-h-80 overflow-auto px-4 py-3 text-fh-meta leading-relaxed whitespace-pre-wrap break-words font-mono text-fh-text-body">
            {result.log}
          </pre>
        </Card>
      )}
    </div>
  );
}
